<!-- src/components/layout/BackgroundPatterns.vue -->
<template>
  <div class="background-patterns">
    <div class="pattern-container">
      <div
        v-for="(pattern, index) in patterns"
        :key="index"
        class="pattern-item"
        :style="getPatternStyle(pattern)"
      >
        <component :is="getIcon(pattern.icon)" class="pattern-icon" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import {
  KeyRound,
  UserCircle2,
  Shield,
  Settings,
  Lock,
  Fingerprint,
  CircleUserRound,
  KeySquare,
} from "lucide-vue-next";

interface Pattern {
  x: number;
  y: number;
  rotation: number;
  scale: number;
  icon: number;
  duration: number;
  delay: number;
}

// 定义图标集合
const icons = [
  KeyRound,
  Shield,
  Settings,
  UserCircle2,
  Lock,
  Fingerprint,
  CircleUserRound,
  KeySquare,
];

// 生成随机图案
const generatePatterns = (
  count: number,
  width: number,
  height: number,
): Pattern[] => {
  return Array.from({ length: count }, () => ({
    x: Math.random() * width,
    y: Math.random() * height,
    rotation: Math.random() * 360,
    scale: 0.5 + Math.random() * 0.5,
    icon: Math.floor(Math.random() * icons.length),
    duration: 15 + Math.random() * 15,
    delay: Math.random() * -20,
  }));
};

const patterns = ref<Pattern[]>([]);
let resizeObserver: ResizeObserver | null = null;

// 获取图案样式
const getPatternStyle = (pattern: Pattern): Record<string, string> => {
  return {
    "--x": `${pattern.x}px`,
    "--y": `${pattern.y}px`,
    "--rotation": `${pattern.rotation}deg`,
    "--scale": pattern.scale.toString(),
    "--duration": `${pattern.duration}s`,
    "--delay": `${pattern.delay}s`,
    left: "0",
    top: "0",
    transform: `translate(var(--x), var(--y)) rotate(var(--rotation)) scale(var(--scale))`,
    animation: `float var(--duration) var(--delay) infinite ease-in-out`,
  };
};

// 获取对应的图标组件
const getIcon = (index: number) => icons[index];

// 处理窗口大小变化
const handleResize = (entries: ResizeObserverEntry[]) => {
  const { width, height } = entries[0].contentRect;
  const count = Math.floor((width * height) / 70000); // 根据屏幕大小调整图案数量
  patterns.value = generatePatterns(count, width, height);
};

onMounted(() => {
  resizeObserver = new ResizeObserver(handleResize);
  const container = document.querySelector(".background-patterns");
  if (container) {
    resizeObserver.observe(container);
  }
});

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
  }
});
</script>

<style scoped lang="less">
.background-patterns {
  position: fixed;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
}

.pattern-container {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    fade(@primary-1, 60%) 0%,
    fade(@grey-2, 60%) 100%
  );
}

.pattern-item {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  will-change: transform;

  .pattern-icon {
    width: 48px;
    height: 48px;
    color: @primary-color;
    opacity: 0.06;
    transition: opacity 0.3s ease;

    &:hover {
      opacity: 0.1;
    }
  }
}

@keyframes float {
  0%,
  100% {
    transform: translate(var(--x), var(--y)) rotate(var(--rotation))
      scale(var(--scale));
  }
  50% {
    transform: translate(calc(var(--x) + 20px), calc(var(--y) - 20px))
      rotate(calc(var(--rotation) + 5deg)) scale(var(--scale));
  }
}

// 移动端优化
@media (max-width: @screen-sm) {
  .pattern-item {
    .pattern-icon {
      width: 32px;
      height: 32px;
    }
  }
}
</style>
